import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Moon, Sun, Search, Menu, X, LogIn, LogOut, User as UserIcon, FileStack } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDarkMode } from "@/lib/useDarkMode";
import { api } from "@/lib/api";
import { CommandPalette } from "@/components/CommandPalette";

export function Header({ user, onLogout }) {
  const [dark, setDark] = useDarkMode();
  const [openCmd, setOpenCmd] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpenCmd(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <header className="glass fixed inset-x-0 top-0 z-50 border-b border-border/40">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white">
            <FileStack className="h-5 w-5" strokeWidth={2} />
          </div>
          <span className="font-cabinet text-xl font-bold tracking-tight">AllDocs</span>
          <span className="label-mono ml-2 hidden sm:inline">v1</span>
        </Link>

        <button
          data-testid="search-cmd-k-trigger"
          onClick={() => setOpenCmd(true)}
          className="hidden md:flex flex-1 max-w-md mx-8 items-center gap-3 rounded-full border border-border bg-secondary/40 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Search className="h-4 w-4" />
          <span className="flex-1 text-left">Search 200+ tools…</span>
          <kbd className="hidden font-mono text-[10px] font-medium sm:inline-block rounded border border-border bg-background px-1.5 py-0.5">⌘K</kbd>
        </button>

        <div className="flex items-center gap-2">
          <button
            data-testid="search-cmd-k-trigger-mobile"
            onClick={() => setOpenCmd(true)}
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-border"
          >
            <Search className="h-4 w-4" />
          </button>
          <button
            data-testid="dark-mode-toggle"
            onClick={() => setDark(!dark)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:bg-secondary"
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user ? (
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
                ) : (
                  <UserIcon className="h-4 w-4" />
                )}
                <span className="text-sm font-medium">{user.name?.split(" ")[0]}</span>
              </div>
              <Button data-testid="logout-btn" onClick={onLogout} variant="ghost" size="sm">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button data-testid="login-btn" onClick={handleLogin} variant="default" size="sm" className="rounded-full bg-brand hover:bg-brand-hover">
              <LogIn className="mr-1.5 h-4 w-4" />
              Sign in
            </Button>
          )}
        </div>
      </div>

      <CommandPalette open={openCmd} onOpenChange={setOpenCmd} onSelect={(id) => { setOpenCmd(false); navigate(`/tool/${id}`); }} />
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 border-t border-border/60 py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-sm text-muted-foreground">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="font-cabinet text-base font-semibold text-foreground">AllDocs — every document tool, one place.</div>
          <div className="font-mono text-xs">© {new Date().getFullYear()} AllDocs · Files auto-deleted in 24h · End-to-end secure</div>
        </div>
      </div>
    </footer>
  );
}
